import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Database,
  IdCard,
  Download,
  Eye,
  Search,
  ShieldCheck,
  UserCheck,
  UserCog,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import {
  ROLES,
  UNMASK_DURATIONS,
  USER_STATUSES,
  type EmployeeRecord,
  type Role,
  type SessionUser,
  type UserAdminView,
  type UserStatus,
} from '@idas/shared';
import { api, ApiError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { EmptyState, ListSkeleton } from '../../components/ui/EmptyState';
import { ReasonField } from '../../components/ui/ReasonField';
import { InlineSpinner, RefreshingOverlay } from '../../components/ui/Refreshing';
import { toast } from '../../components/ui/Toast';

const STATUS_STYLES: Record<UserStatus, string> = {
  ACTIVE: 'border-ok/30 bg-ok/10 text-ok',
  SUSPENDED: 'border-warn/30 bg-warn/10 text-warn',
  DISABLED: 'border-danger/30 bg-danger/10 text-danger',
};

/**
 * One-click justifications for the audit log.
 *
 * The reason field is mandatory because "who granted this, and why" is the
 * question an audit asks first. But typing a sentence for every routine
 * approval is friction that gets satisfied with keyboard mash — presets give a
 * real answer in one click, and the field stays editable for anything else.
 */
const REASON_PRESETS = [
  'Approved by their line manager.',
  'Joined the team and needs reporting access.',
  'Access no longer required for their role.',
  'Left the company.',
];

const APPROVE_PRESETS = [
  'Approved by their line manager.',
  'Joined the analytics team.',
  'Needs reporting access for their role.',
];

const EXPORT_PRESETS = [
  'Needs to supply monthly figures to Finance.',
  'Approved by their line manager for reporting duties.',
  'No longer required for their role.',
];

const UNMASK_PRESETS = [
  'Reconciling duplicate student records with the registrar.',
  'Investigating a data quality issue reported by the team.',
  'Responding to a subject access request.',
];

const REFUSE_PRESETS = [
  'This data is not required for your role. Ask your manager to request it if that changes.',
  'Please raise this through your line manager first.',
  'Duplicate request — you already have access under another account.',
];

const ROLE_BLURB: Record<Role, string> = {
  ANALYST: 'Run queries and read their own history. Cannot export.',
  ANALYST_SENIOR: 'Everything an analyst can do, plus downloading results.',
  AUDITOR: 'Read the audit log and everyone’s history. Deliberately cannot run queries.',
  ADMIN: 'Full control, including policy, connections and other people’s access.',
};

export function UsersTab({ me }: { me: SessionUser }) {
  const queryClient = useQueryClient();
  const canWrite = me.permissions.includes('user:write');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [editing, setEditing] = useState<UserAdminView | null>(null);
  const [deciding, setDeciding] = useState<UserAdminView | null>(null);

  const pending = useQuery({ queryKey: ['users', 'pending'], queryFn: api.users.pending });
  const users = useQuery({
    queryKey: ['users', statusFilter, search],
    queryFn: () => api.users.list({ status: statusFilter || undefined, search: search || undefined }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  return (
    <div className="space-y-5">
      {pending.isLoading && (
        <section className="rounded-lg border border-edge bg-surface-raised">
          <ListSkeleton rows={2} height={44} />
        </section>
      )}

      {/* ── Approval queue ─────────────────────────────────────────────────
          Given top billing because it is the step that gates everything: a
          new employee cannot run a single query until someone acts here, and
          an approval queue nobody notices becomes a support ticket. */}
      {pending.data && pending.data.users.length > 0 && (
        <section className="rounded-lg border border-warn/30 bg-warn/5">
          <header className="flex items-center gap-2 border-b border-warn/20 px-4 py-2.5">
            <UserCheck className="h-3.5 w-3.5 text-warn" />
            <h2 className="text-xs font-semibold text-ink">
              {pending.data.users.length} awaiting approval
            </h2>
            <span className="text-2xs text-ink-muted">
              Verified by the portal, not yet granted access here.
            </span>
          </header>

          <div className="divide-y divide-warn/15">
            {pending.data.users.map((user) => (
              <div key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-2xs font-medium text-ink">{user.name}</p>
                  <p className="truncate text-2xs text-ink-muted">
                    {user.email} · {user.employeeId}
                    {user.department && ` · ${user.department}`}
                  </p>
                  <EmployeeLine employee={user.employee} />
                </div>

                {user.portalGroups.length > 0 && (
                  <div className="flex flex-wrap gap-1" title="Groups the portal reported. Informational only.">
                    {user.portalGroups.slice(0, 3).map((group) => (
                      <span key={group} className="chip border-edge bg-surface text-ink-muted">
                        {group}
                      </span>
                    ))}
                  </div>
                )}

                <span className="text-2xs text-ink-faint">
                  first seen {new Date(user.firstSeenAt).toLocaleDateString()}
                </span>

                {canWrite && (
                  <button className="btn btn-primary" onClick={() => setDeciding(user)}>
                    Review request
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Everyone ───────────────────────────────────────────────────── */}
      <section className="panel rounded-lg">
        <header className="flex flex-wrap items-center gap-2 border-b border-edge px-3 py-2">
          <Users className="h-3.5 w-3.5 text-ink-muted" />
          <h2 className="text-xs font-semibold text-ink">People</h2>

          <div className="flex-1" />

          <div className="relative w-52">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
            <input
              className="input py-1 pl-7 pr-7"
              placeholder="Search name or email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {/* In the field itself, where the typing is — a spinner somewhere
                else on the page does not connect to the search. */}
            {users.isFetching && !users.isLoading && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2">
                <InlineSpinner />
              </span>
            )}
          </div>

          <select
            className="input w-auto py-1"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as UserStatus | '')}
          >
            <option value="">All statuses</option>
            {USER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.toLowerCase()}
              </option>
            ))}
          </select>
        </header>

        {users.isLoading && <ListSkeleton rows={6} height={44} />}

        {users.data?.users.length === 0 && (
          <EmptyState
            icon={Users}
            title="Nobody matches"
            body={
              search || statusFilter
                ? 'Try clearing the search or the status filter.'
                : 'People appear here the first time they arrive from the employee portal.'
            }
          />
        )}

        <RefreshingOverlay active={users.isFetching && !users.isLoading}>
        <div className="divide-y divide-edge">
          {users.data?.users.map((user) => (
            <div key={user.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-2xs font-medium text-ink">
                  {user.name}
                  {user.id === me.id && (
                    <span className="ml-1.5 text-2xs font-normal text-ink-faint">(you)</span>
                  )}
                </p>
                <p className="truncate text-2xs text-ink-muted">{user.email}</p>
                <EmployeeLine employee={user.employee} />
              </div>

              <span className={clsx('chip', STATUS_STYLES[user.status])}>
                {user.status.toLowerCase()}
              </span>

              {/* HR says they have gone but the console has not caught up. The
                  single most actionable thing this screen can tell an
                  administrator, so it is not buried in the modal. */}
              {user.employee && !user.employee.employed && user.status === 'ACTIVE' && (
                <span
                  className="chip border-danger/40 bg-danger/10 text-danger"
                  title={
                    user.employee.exitDate
                      ? `HR records them as having left on ${new Date(
                          user.employee.exitDate,
                        ).toLocaleDateString()}, but this account is still active.`
                      : 'HR does not record them as employed, but this account is still active.'
                  }
                >
                  left the company
                </span>
              )}

              <span className="chip border-edge bg-surface-sunken text-ink-muted">
                {user.role.replace('_', ' ').toLowerCase()}
              </span>

              {/* Admins are exempt from the per-connection check, so a count
                  next to their name would be misleading. */}
              {user.role !== 'ADMIN' && user.status === 'ACTIVE' && (
                <span
                  className={clsx(
                    'chip',
                    user.dataSourceAccess.length
                      ? 'border-edge bg-surface-sunken text-ink-muted'
                      : 'border-warn/30 bg-warn/10 text-warn',
                  )}
                  title={
                    user.dataSourceAccess.length
                      ? `May query ${user.dataSourceAccess.length} connection(s)`
                      : 'Approved, but not granted any database — every query will be refused.'
                  }
                >
                  <Database className="h-2.5 w-2.5" />
                  {user.dataSourceAccess.length || 'none'}
                </span>
              )}

              {user.unmask.until && (
                <span
                  className="chip border-lock/40 bg-lock/10 text-lock"
                  title={`Sees unmasked values until ${new Date(user.unmask.until).toLocaleString()} — granted by ${user.unmask.grantedBy}`}
                >
                  <Eye className="h-2.5 w-2.5" />
                  unmasked · {remainingLabel(user.unmask.until)}
                </span>
              )}

              <span className="w-24 text-right text-2xs tabular-nums text-ink-faint">
                {user.queryCount.toLocaleString()} queries
              </span>

              <span className="w-36 text-right text-2xs text-ink-faint">
                {user.lastLoginAt
                  ? `seen ${new Date(user.lastLoginAt).toLocaleDateString()}`
                  : 'never signed in'}
              </span>

              {canWrite && (
                <button
                  className="btn btn-ghost px-1.5"
                  onClick={() => setEditing(user)}
                  aria-label={`Change access for ${user.name}`}
                >
                  <UserCog className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        </RefreshingOverlay>
      </section>

      {editing && (
        <AccessModal
          user={editing}
          isSelf={editing.id === me.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}

      {deciding && (
        <DecisionModal
          user={deciding}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            invalidate();
            setDeciding(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Their real job, on one line, for the People list.
 *
 * The console role sits in its own chip and says what they may do here;
 * this says who they actually are. Rendered as the subtitle because it is
 * what an administrator scans for when looking someone up.
 */
function EmployeeLine({ employee }: { employee: EmployeeRecord | null }) {
  if (!employee) return null;
  // Role before department, matching how the portal introduces someone.
  const parts = [employee.role ?? employee.designation, employee.wing, employee.department].filter(
    Boolean,
  );
  if (!parts.length) return null;
  return (
    <span className="truncate text-2xs text-ink-faint">
      {parts.join(' · ')}
      {employee.employeeCode ? ` · ${employee.employeeCode}` : ''}
    </span>
  );
}

/** One label/value row inside {@link EmployeeCard}. */
function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-2xs text-ink-faint">{label}</dt>
      <dd className="min-w-0 flex-1 text-2xs text-ink">{value}</dd>
    </div>
  );
}

/**
 * The employee record, as the company's own systems hold it.
 *
 * Shown when deciding on a request, because "should this person read the
 * finance database?" is unanswerable from an email address alone. Read live
 * from the application database — nothing here is stored by this system, so
 * it cannot go stale.
 */
function EmployeeCard({ employee }: { employee: EmployeeRecord | null }) {
  if (!employee) {
    return (
      <p className="rounded-md border border-edge bg-surface-sunken p-2.5 text-2xs leading-relaxed text-ink-muted">
        No matching employee record was found in the company database. That is worth checking
        before granting access.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-edge bg-surface-sunken p-3">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <IdCard className="h-3 w-3 text-ink-faint" />
        <p className="text-2xs font-medium text-ink-muted">Company record</p>
        {employee.isHod && (
          <span className="chip border-accent/30 bg-accent/10 text-accent">
            heads this department
          </span>
        )}
        {!employee.employed && (
          <span className="chip border-danger/30 bg-danger/10 text-danger">
            no longer employed
          </span>
        )}
      </div>

      <dl className="space-y-1">
        <Detail label="Name" value={employee.name} />
        <Detail label="Role" value={employee.role} />
        {/* Only when it says something the role did not — for most people the
            designation repeats the role almost verbatim. */}
        <Detail
          label="Designation"
          value={
            employee.designation &&
            employee.designation.toLowerCase() !== (employee.role ?? '').toLowerCase()
              ? employee.designation
              : null
          }
        />
        <Detail
          label="Also tagged"
          value={employee.otherRoles.length ? employee.otherRoles.join(' · ') : null}
        />
        <Detail label="Wing" value={employee.wing} />
        <Detail label="Department" value={employee.department} />
        <Detail label="Reports to" value={employee.reportingHead} />
        <Detail label="Employment" value={employee.employmentStatus} />
        <Detail label="Employee code" value={employee.employeeCode} />
        <Detail label="Official email" value={employee.officialEmail} />
        <Detail
          label="Joined"
          value={employee.joinedOn ? new Date(employee.joinedOn).toLocaleDateString() : null}
        />
        <Detail
          label="Left"
          value={employee.exitDate ? new Date(employee.exitDate).toLocaleDateString() : null}
        />
      </dl>
    </div>
  );
}

/**
 * The set of connections a person may query.
 *
 * Separate from the role: a role says what someone may *do* (run, export,
 * audit), this says what they may do it *to*. Most organisations have one
 * database that is fine for analysts and one that very much is not, and a
 * single "has access" flag cannot express that.
 *
 * Disabled connections are still listed, greyed, so an admin who grants one
 * is not left wondering why the analyst still cannot see it.
 */
function ConnectionPicker({
  selected,
  onChange,
  showError,
  emptyIsWarning,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  showError?: boolean;
  emptyIsWarning?: boolean;
}) {
  const sources = useQuery({
    queryKey: ['datasources', 'all'],
    queryFn: api.datasources.listAll,
  });

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const all = sources.data?.datasources ?? [];

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label className="text-2xs font-medium text-ink-muted">
          Which databases may they query?
        </label>
        {all.length > 1 && (
          <div className="flex gap-2">
            <button
              className="text-2xs text-accent hover:underline"
              onClick={() => onChange(all.map((d) => d.id))}
            >
              Select all
            </button>
            <button
              className="text-2xs text-ink-faint hover:underline"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {sources.isLoading ? (
        <ListSkeleton rows={2} height={38} />
      ) : all.length === 0 ? (
        <p className="rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
          No connections are configured yet. Add one under Connections — until then there is
          nothing to grant.
        </p>
      ) : (
        <div className="space-y-1">
          {all.map((source) => {
            const on = selected.includes(source.id);
            return (
              <button
                key={source.id}
                className={clsx(
                  'flex w-full items-start gap-2 rounded-md border p-2 text-left transition-colors',
                  on ? 'border-accent bg-accent/5' : 'border-edge hover:bg-surface-sunken',
                )}
                onClick={() => toggle(source.id)}
              >
                <span
                  className={clsx(
                    'mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center rounded-[3px] border',
                    on ? 'border-accent bg-accent text-white' : 'border-edge-strong',
                  )}
                >
                  {on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-2xs font-medium text-ink">{source.name}</span>
                    <span className="chip border-edge bg-surface-sunken text-ink-faint">
                      {source.engine.toLowerCase()}
                    </span>
                    {!source.enabled && (
                      <span className="chip border-warn/30 bg-warn/10 text-warn">disabled</span>
                    )}
                  </span>
                  <span className="block truncate text-2xs text-ink-muted">
                    {source.database}
                    {source.description ? ` · ${source.description}` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selected.length === 0 && all.length > 0 && (
        <p
          className={clsx(
            'mt-1.5 text-2xs leading-relaxed',
            showError && emptyIsWarning ? 'text-warn' : 'text-ink-faint',
          )}
        >
          {emptyIsWarning
            ? 'With none selected they can sign in but every query will be refused. Pick at least one.'
            : 'None selected — they will not be able to query anything.'}
        </p>
      )}
    </div>
  );
}

/**
 * Approve or refuse a request for console access.
 *
 * Both outcomes are one decision and one screen. A refusal takes the same
 * note field as an approval, because that text is shown to the person who
 * asked — being told "no" without a reason just produces a second request.
 */
function DecisionModal({
  user,
  onClose,
  onDecided,
}: {
  user: UserAdminView;
  onClose: () => void;
  onDecided: () => void;
}) {
  const [approve, setApprove] = useState(true);
  const [role, setRole] = useState<Role>('ANALYST');
  const [note, setNote] = useState('');
  const [dataSourceIds, setDataSourceIds] = useState<string[]>(user.dataSourceAccess);

  const decide = useMutation({
    mutationFn: () => api.users.decideAccess(user.id, { approve, role, note, dataSourceIds }),
    onSuccess: () => {
      toast.success(
        approve
          ? `${user.name} can now use the console as ${role.replace('_', ' ').toLowerCase()}, ` +
            `with ${dataSourceIds.length} ${dataSourceIds.length === 1 ? 'connection' : 'connections'}.`
          : `${user.name}'s request was refused. They will see your reason.`,
      );
      onDecided();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save the decision.'),
  });

  const noteRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [attempted, setAttempted] = useState(false);
  const noteTooShort = note.trim().length < 10;
  // Administrators reach every connection by role, so an empty selection for
  // them is correct rather than an oversight.
  const needsConnection = approve && role !== 'ADMIN' && dataSourceIds.length === 0;

  // Clickable even when incomplete — see the note on attemptSave in AccessModal.
  const attemptDecide = () => {
    if (!noteTooShort && !needsConnection) {
      decide.mutate();
      return;
    }
    setAttempted(true);
    const target = noteTooShort ? noteRef.current : pickerRef.current;
    if (noteTooShort) noteRef.current?.focus();
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const presets = approve ? APPROVE_PRESETS : REFUSE_PRESETS;

  return (
    <Modal
      title={`Access request — ${user.name}`}
      description={`${user.email} · employee ${user.employeeId}${
        user.access.requestedAt
          ? ` · asked ${new Date(user.access.requestedAt).toLocaleString()}`
          : ''
      }`}
      onClose={onClose}
      footer={
        <>
          {attempted && (noteTooShort || needsConnection) && (
            <span className="mr-auto max-w-[20rem] text-2xs leading-snug text-warn">
              {noteTooShort
                ? `Add a note of at least 10 characters (${note.trim().length} so far).`
                : 'Choose at least one database they may query.'}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Decide later
          </button>
          <button
            className={clsx(
              'btn',
              approve
                ? 'btn-primary'
                : 'border-transparent bg-danger text-white hover:bg-danger/90',
            )}
            disabled={decide.isPending}
            onClick={attemptDecide}
          >
            {decide.isPending ? 'Saving…' : approve ? 'Approve access' : 'Refuse request'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <EmployeeCard employee={user.employee} />

        {/* What they asked for, next — together with the record above it is
            what answers "should this be approved?", and burying either under
            role options invites rubber-stamping. */}
        {user.access.purpose ? (
          <div className="rounded-md border border-accent/30 bg-accent/5 p-3">
            <p className="mb-1 text-2xs font-medium text-accent">Why they need access</p>
            <p className="text-2xs leading-relaxed text-ink">“{user.access.purpose}”</p>
          </div>
        ) : (
          <p className="rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
            They have not stated a purpose. Worth asking before approving.
          </p>
        )}

        {user.portalGroups.length > 0 && (
          <div>
            <p className="mb-1 text-2xs font-medium text-ink-muted">
              Groups the portal reports
            </p>
            <div className="flex flex-wrap gap-1">
              {user.portalGroups.map((group) => (
                <span key={group} className="chip border-edge bg-surface-sunken text-ink-muted">
                  {group}
                </span>
              ))}
            </div>
            <p className="mt-1 text-2xs text-ink-faint">
              Informational only — the portal cannot grant anything here.
            </p>
          </div>
        )}

        <div className="flex gap-1.5">
          <button
            className={clsx('btn flex-1', approve && 'btn-primary')}
            onClick={() => setApprove(true)}
          >
            Approve
          </button>
          <button
            className={clsx(
              'btn flex-1',
              !approve && 'border-transparent bg-danger text-white hover:bg-danger/90',
            )}
            onClick={() => setApprove(false)}
          >
            Refuse
          </button>
        </div>

        {approve && (
          <div>
            <label className="mb-1.5 block text-2xs font-medium text-ink-muted">
              Grant which role?
            </label>
            <div className="space-y-1">
              {ROLES.map((option) => (
                <button
                  key={option}
                  className={clsx(
                    'flex w-full items-start gap-2 rounded-md border p-2 text-left transition-colors',
                    role === option ? 'border-accent bg-accent/5' : 'border-edge hover:bg-surface-sunken',
                  )}
                  onClick={() => setRole(option)}
                >
                  <span
                    className={clsx(
                      'mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full border',
                      role === option ? 'border-accent bg-accent' : 'border-edge-strong',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-2xs font-medium text-ink">
                      {option.replace('_', ' ').toLowerCase()}
                    </span>
                    <span className="block text-2xs leading-relaxed text-ink-muted">
                      {ROLE_BLURB[option]}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {approve && (
          <div ref={pickerRef}>
            {role === 'ADMIN' ? (
              <p className="rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
                Administrators can reach every connection, including ones added later. That is
                part of what the role means — there is nothing to choose here.
              </p>
            ) : (
              <ConnectionPicker
                selected={dataSourceIds}
                onChange={setDataSourceIds}
                showError={attempted}
                emptyIsWarning
              />
            )}
          </div>
        )}

        <ReasonField
          ref={noteRef}
          id="decision-note"
          label={approve ? 'Why are you granting this?' : 'What should they be told?'}
          value={note}
          onChange={setNote}
          minLength={10}
          presets={presets}
          placeholder={
            approve
              ? 'e.g. Joined the Analytics team on 3 Aug, approved by their manager.'
              : 'e.g. This data is not needed for your role. Ask your manager to raise a request if that changes.'
          }
          showError={attempted}
          hint={
            approve
              ? 'Recorded in the audit log against your name.'
              : 'Shown to them on the request screen, and recorded in the audit log.'
          }
        />
      </div>
    </Modal>
  );
}

function AccessModal({
  user,
  isSelf,
  onClose,
  onSaved,
}: {
  user: UserAdminView;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState<Role>(user.role);
  const [status, setStatus] = useState<UserStatus>(user.status);
  const [reason, setReason] = useState('');
  const [dataSourceIds, setDataSourceIds] = useState<string[]>(user.dataSourceAccess);

  const connectionsChanged =
    dataSourceIds.length !== user.dataSourceAccess.length ||
    dataSourceIds.some((id) => !user.dataSourceAccess.includes(id));

  const save = useMutation({
    mutationFn: async () => {
      // Two endpoints, one button. Role/status and connection grants are
      // separate concerns server-side — each gets its own audit entry — but an
      // administrator adjusting someone's access thinks of it as one change.
      if (role !== user.role || status !== user.status) {
        await api.users.update(user.id, { role, status, reason });
      }
      if (connectionsChanged) {
        await api.users.setDataSourceAccess(user.id, { dataSourceIds, reason });
      }
    },
    onSuccess: () => {
      toast.success(`Updated access for ${user.name}.`);
      onSaved();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save the change.'),
  });

  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [attempted, setAttempted] = useState(false);

  const changed = role !== user.role || status !== user.status || connectionsChanged;
  const reasonTooShort = reason.trim().length < 10;

  const blocker = !changed
    ? isSelf
      ? 'You cannot change your own role or status. Ask another administrator.'
      : 'Nothing has changed yet — pick a different status, role or connection above.'
    : reasonTooShort
      ? `Add a reason of at least 10 characters (${reason.trim().length} so far). It is recorded against your name.`
      : null;

  /**
   * The button stays clickable even when the form is incomplete.
   *
   * A disabled button explains nothing: you press it, nothing happens, and
   * there is no way to find out why. Letting the click through and pointing
   * at the missing field turns a dead end into an instruction.
   */
  const attemptSave = () => {
    if (!blocker) {
      save.mutate();
      return;
    }
    setAttempted(true);
    if (changed && reasonTooShort) {
      reasonRef.current?.focus();
      reasonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  // Losing ACTIVE ends every session immediately, so it is worth saying so
  // before the click rather than explaining it afterwards.
  const willTerminateSessions = user.status === 'ACTIVE' && status !== 'ACTIVE';
  const promotingToAdmin = role === 'ADMIN' && user.role !== 'ADMIN';

  return (
    <Modal
      title={`Access for ${user.name}`}
      description={`${user.email} · employee ${user.employeeId}`}
      onClose={onClose}
      footer={
        <>
          {blocker && attempted && (
            <span className="mr-auto max-w-[22rem] text-2xs leading-snug text-warn">
              {blocker}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={attemptSave}
          >
            {save.isPending ? 'Saving…' : 'Save change'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <EmployeeCard employee={user.employee} />

        <div>
          <label className="mb-1.5 block text-2xs font-medium text-ink-muted">Status</label>
          <div className="flex gap-1.5">
            {USER_STATUSES.map((option) => (
              <button
                key={option}
                className={clsx('btn flex-1', status === option && 'btn-primary')}
                onClick={() => setStatus(option)}
                disabled={isSelf && option !== 'ACTIVE'}
                title={isSelf && option !== 'ACTIVE' ? 'You cannot suspend your own account.' : undefined}
              >
                {option.toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-2xs font-medium text-ink-muted">Role</label>
          <div className="space-y-1">
            {ROLES.map((option) => (
              <button
                key={option}
                className={clsx(
                  'flex w-full items-start gap-2 rounded-md border p-2 text-left transition-colors',
                  role === option
                    ? 'border-accent bg-accent/5'
                    : 'border-edge hover:bg-surface-sunken',
                  isSelf && option !== 'ADMIN' && 'cursor-not-allowed opacity-40',
                )}
                onClick={() => !(isSelf && option !== 'ADMIN') && setRole(option)}
                disabled={isSelf && option !== 'ADMIN'}
              >
                <span
                  className={clsx(
                    'mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full border',
                    role === option ? 'border-accent bg-accent' : 'border-edge-strong',
                  )}
                />
                <span className="min-w-0">
                  <span className="block text-2xs font-medium text-ink">
                    {option.replace('_', ' ').toLowerCase()}
                  </span>
                  <span className="block text-2xs leading-relaxed text-ink-muted">
                    {ROLE_BLURB[option]}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {isSelf && (
            <p className="mt-1.5 text-2xs text-ink-faint">
              You cannot change your own role — an admin who demotes themselves locks everyone out
              of administration.
            </p>
          )}
        </div>

        {role === 'ADMIN' ? (
          <p className="rounded-md border border-edge bg-surface-sunken p-2.5 text-2xs leading-relaxed text-ink-muted">
            Administrators reach every connection by virtue of the role, so there is nothing to
            select. Any grants set here are kept, and take effect again if the role changes.
          </p>
        ) : (
          <ConnectionPicker selected={dataSourceIds} onChange={setDataSourceIds} />
        )}

        {promotingToAdmin && (
          <p className="flex items-start gap-1.5 rounded-md border border-danger/30 bg-danger/5 p-2.5 text-2xs leading-relaxed text-danger">
            <ShieldCheck className="mt-px h-3 w-3 shrink-0" />
            This grants control over policy, connections and everyone else’s access. It is logged
            as a critical event.
          </p>
        )}

        {willTerminateSessions && (
          <p className="rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
            Every session this person currently has will be ended immediately, including any query
            they are running.
          </p>
        )}

        <ReasonField
          ref={reasonRef}
          id="access-reason"
          label="Why is this changing?"
          value={reason}
          onChange={setReason}
          minLength={10}
          presets={REASON_PRESETS}
          placeholder="e.g. Joined the Analytics team on 3 Aug, approved by their manager."
          showError={attempted && changed}
          hint="Written to the audit log against your name."
        />

        <ExportSection user={user} onChanged={onSaved} />

        <UnmaskSection user={user} isSelf={isSelf} onChanged={onSaved} />
      </div>
    </Modal>
  );
}

/**
 * Whether this person may download query results.
 *
 * Three states rather than a switch: the role sets a default, and an override
 * can grant or withdraw regardless — so one analyst can be given downloads
 * without a promotion, and one senior analyst can lose them without a demotion.
 */
function ExportSection({
  user,
  onChanged,
}: {
  user: UserAdminView;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [pendingChoice, setPendingChoice] = useState<boolean | null | undefined>(undefined);
  const [attempted, setAttempted] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const save = useMutation({
    mutationFn: (override: boolean | null) =>
      api.users.setExportPrivilege(user.id, { override, reason: reason.trim() }),
    onSuccess: (updated) => {
      toast.success(
        updated.exportPrivilege.effective
          ? `${user.name} can now download results.`
          : `${user.name} can no longer download results.`,
      );
      onChanged();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not change the privilege.'),
  });

  const { override, fromRole, effective } = user.exportPrivilege;
  const choice = pendingChoice === undefined ? override : pendingChoice;

  const attemptSave = () => {
    if (reason.trim().length >= 10) {
      save.mutate(choice ?? null);
      return;
    }
    setAttempted(true);
    reasonRef.current?.focus();
    reasonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const OPTIONS: Array<{ value: boolean | null; label: string; hint: string }> = [
    {
      value: null,
      label: 'Follow role',
      hint: fromRole
        ? `Their role allows downloads.`
        : `Their role does not allow downloads.`,
    },
    { value: true, label: 'Allow', hint: 'Can download results regardless of role.' },
    { value: false, label: 'Block', hint: 'Cannot download, even if the role allows it.' },
  ];

  return (
    <div className="rounded-md border border-edge bg-surface-sunken p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <Download className="h-3.5 w-3.5 text-ink-muted" />
        <span className="text-2xs font-semibold text-ink">Download data</span>
        <span
          className={clsx(
            'chip ml-auto',
            effective
              ? 'border-warn/30 bg-warn/10 text-warn'
              : 'border-edge bg-surface text-ink-muted',
          )}
        >
          {effective ? 'allowed' : 'blocked'}
        </span>
      </div>

      <p className="mb-2.5 text-2xs leading-relaxed text-ink-muted">
        Running a query keeps data inside this console. Downloading puts a file on a laptop —
        every export is recorded with the reason the analyst gives.
      </p>

      <div className="flex gap-1.5">
        {OPTIONS.map((option) => (
          <button
            key={String(option.value)}
            className={clsx('btn flex-1', choice === option.value && 'btn-primary')}
            onClick={() => setPendingChoice(option.value)}
            title={option.hint}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="mt-1.5 text-2xs text-ink-faint">
        {OPTIONS.find((o) => o.value === choice)?.hint}
      </p>

      {choice !== override && (
        <div className="mt-2.5">
          <ReasonField
            ref={reasonRef}
            id={`export-priv-reason-${user.id}`}
            label="Why is this changing?"
            value={reason}
            onChange={setReason}
            minLength={10}
            presets={EXPORT_PRESETS}
            rows={2}
            placeholder="e.g. Needs to supply monthly figures to Finance."
            showError={attempted}
            hint="Recorded in the audit log against your name."
          />
          <button
            className="btn btn-primary mt-2"
            disabled={save.isPending}
            onClick={attemptSave}
          >
            {save.isPending ? 'Saving…' : 'Apply'}
          </button>
        </div>
      )}

      {user.exportPrivilege.decidedBy && choice === override && (
        <p className="mt-1.5 text-2xs text-ink-faint">
          Last set by {user.exportPrivilege.decidedBy}
          {user.exportPrivilege.reason ? ` — “${user.exportPrivilege.reason}”` : ''}
        </p>
      )}
    </div>
  );
}

/**
 * Time-bound access to values that masking rules would normally hide.
 *
 * Its own section with its own actions, because it is not a variation on
 * "change this person's role" — it is a separate, higher-stakes decision that
 * gets its own audit entry and its own justification.
 */
function UnmaskSection({
  user,
  isSelf,
  onChanged,
}: {
  user: UserAdminView;
  isSelf: boolean;
  onChanged: () => void;
}) {
  const [hours, setHours] = useState<number>(UNMASK_DURATIONS[1].hours);
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const grant = useMutation({
    mutationFn: () => api.users.grantUnmask(user.id, { hours, reason }),
    onSuccess: () => {
      toast.warning(`${user.name} can now see unmasked values. This is logged.`);
      onChanged();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not grant access.'),
  });

  const revoke = useMutation({
    mutationFn: () => api.users.revokeUnmask(user.id, 'Revoked by administrator'),
    onSuccess: () => {
      toast.success('Unmasked access revoked.');
      onChanged();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not revoke access.'),
  });

  const active = Boolean(user.unmask.until);
  const tooShort = reason.trim().length < 15;

  const attemptGrant = () => {
    if (!tooShort) {
      grant.mutate();
      return;
    }
    setAttempted(true);
    reasonRef.current?.focus();
    reasonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  return (
    <div className="rounded-md border border-lock/30 bg-lock/5 p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <Eye className="h-3.5 w-3.5 text-lock" />
        <span className="text-2xs font-semibold text-ink">Unmasked data access</span>
      </div>

      {active ? (
        <>
          <p className="text-2xs leading-relaxed text-ink">
            Sees stored values instead of masked ones until{' '}
            <strong>{new Date(user.unmask.until!).toLocaleString()}</strong> (
            {remainingLabel(user.unmask.until!)} left).
          </p>
          {user.unmask.reason && (
            <p className="mt-1.5 border-l-2 border-lock/40 pl-2 text-2xs italic text-ink-muted">
              “{user.unmask.reason}” — granted by {user.unmask.grantedBy}
            </p>
          )}
          <button
            className="btn mt-3"
            onClick={() => revoke.mutate()}
            disabled={revoke.isPending}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke now'}
          </button>
        </>
      ) : (
        <>
          <p className="text-2xs leading-relaxed text-ink-muted">
            Lifts <strong>masking</strong> rules for this person, for a fixed period. Columns an
            administrator has <strong>denied</strong> stay hidden — this is not a way around them.
            Every query that returns unmasked values is recorded separately in the audit log.
          </p>

          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {UNMASK_DURATIONS.map((option) => (
              <button
                key={option.hours}
                className={clsx('btn', hours === option.hours && 'btn-primary')}
                onClick={() => setHours(option.hours)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-2.5">
            <ReasonField
              ref={reasonRef}
              id={`unmask-reason-${user.id}`}
              label="Why does this person need unmasked values?"
              value={reason}
              onChange={setReason}
              minLength={15}
              presets={UNMASK_PRESETS}
              tone="lock"
              rows={2}
              placeholder="e.g. Reconciling duplicate student records with the registrar, ticket OPS-4412."
              showError={attempted}
              hint="Recorded as a critical event against your name."
            />
          </div>

          {isSelf && (
            <p className="mt-1.5 text-2xs leading-relaxed text-warn">
              You are granting this to yourself. That is allowed, but it is flagged in the audit
              log — where possible, have another administrator do it.
            </p>
          )}

          <button
            className="btn mt-2 border-transparent bg-lock text-white hover:bg-lock/90"
            disabled={grant.isPending}
            onClick={attemptGrant}
          >
            {grant.isPending ? 'Granting…' : 'Grant unmasked access'}
          </button>
        </>
      )}
    </div>
  );
}

function remainingLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
